/**
 * 好み選択オンボーディングの3群割当。
 *
 * | 群 | オンボーディング | メッセージ一覧の絞り込み |
 * |---|---|---|
 * | control | なし | なし |
 * | onboardingOnly | あり | なし |
 * | onboardingWithFilter | あり | あり |
 *
 * 読み方は次の2つ。**onboardingOnly と onboardingWithFilter を合算して control と
 * 比べてはいけない**（絞り込みの効果が混ざり、オンボーディング単体の値にならない）。
 *
 * - オンボーディング単体の効果: onboardingOnly − control
 * - 絞り込み単体の効果: onboardingWithFilter − onboardingOnly
 *
 * 「オンボなし × 絞り込みあり」の群は作らない。好みが未登録だと BE が絞り込まずに
 * 全件返すため control と区別できない。
 *
 * ★実験を2つに分けて独立にハッシュしない。1つのハッシュを3分割することで、
 *   集計時に BigQuery 側で1つの式から3群を復元できる。式は `~/lib/abTest` 参照。
 */
import { PREFERENCE_ONBOARDING_EXPERIMENT } from "~/constants/experiments";
import { getExperimentBucket } from "~/lib/abTest";
import { captureError } from "~/lib/sentry/captureError";

export type PreferenceOnboardingArm =
  | "control"
  | "onboardingOnly"
  | "onboardingWithFilter";

/**
 * 群の境界（バケットは 0〜99）。
 *
 * 100 は 3 で割り切れないため、余りの1バケットは onboardingOnly に寄せている。
 * この群は2つの対比の両方に登場するので、一番細くしてはいけない。
 */
const CONTROL_UPPER = 33; // 0〜32   → 33バケット
const ONBOARDING_ONLY_UPPER = 67; // 33〜66  → 34バケット
//                                   67〜99  → 33バケット

/**
 * 指定ユーザーが属する群を返す。
 *
 * ハッシュの算出に失敗した場合は control として扱う。実験のために既存の挙動を
 * 壊さないことを優先する（`isInTreatmentGroup` と同じ方針）。
 */
export const resolvePreferenceOnboardingArm = async (
  userId: string,
): Promise<PreferenceOnboardingArm> => {
  try {
    const bucket = await getExperimentBucket(
      PREFERENCE_ONBOARDING_EXPERIMENT,
      userId,
    );
    if (bucket < CONTROL_UPPER) return "control";
    if (bucket < ONBOARDING_ONLY_UPPER) return "onboardingOnly";
    return "onboardingWithFilter";
  } catch (error) {
    captureError(error, "preferenceOnboardingExperiment: 群の算出に失敗");
    return "control";
  }
};

/**
 * 新規登録完了後の遷移先を返す。3つの登録経路すべてがこれを通る。
 *
 * control は従来どおり /discover に着地する。オンボーディングを見せる群は
 * /preference-onboarding を挟み、「アプリをはじめる」で /discover へ進む。
 */
export const resolveRouteAfterRegister = async (
  userId: string,
): Promise<"/discover" | "/preference-onboarding"> =>
  (await resolvePreferenceOnboardingArm(userId)) === "control"
    ? "/discover"
    : "/preference-onboarding";

/** メッセージ一覧で好み絞り込みAPIを使う群か（#1124 から参照する） */
export const shouldUsePreferenceFilteredConversationList = async (
  userId: string,
): Promise<boolean> =>
  (await resolvePreferenceOnboardingArm(userId)) === "onboardingWithFilter";
